import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'
import Button from '../components/Button'
import { useLanguageStore } from '../stores/languageStore'
import LocaleToggle from '../components/LocaleToggle'
import { Edit, Trash2 } from 'lucide-react'

const Posts = () => {
  const [showOtherLocales, setShowOtherLocales] = useState(false)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { currentLanguageCode } = useLanguageStore()

  const { data: postsData, isLoading } = useQuery({
    queryKey: ['posts', currentLanguageCode, showOtherLocales],
    queryFn: async () => {
      const locale = currentLanguageCode || 'sr'
      const response = await api.get('/posts', {
        params: {
          locale: locale,
          include_other_locales: showOtherLocales,
        },
      })
      return response.data
    },
    placeholderData: (previousData) => previousData,
  })

  const deleteMutation = useMutation({
    mutationFn: async (postId) => {
      await api.delete(`/posts/${postId}`)
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['posts', currentLanguageCode])
    },
  })

  const handleDelete = async (post) => {
    const translation = post.translations?.find(
      (t) => t.locale === currentLanguageCode
    )
    const title = translation?.title || post.id
    if (window.confirm(`Are you sure you want to delete "${title}"?`)) {
      deleteMutation.mutate(post.id)
    }
  }

  const posts = postsData?.data || []

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Posts</h1>
        <div className="flex gap-3">
          <LocaleToggle
            enabled={showOtherLocales}
            onChange={setShowOtherLocales}
          />
          <Button onClick={() => navigate('/posts/new')}>Add Post</Button>
        </div>
      </div>

      {isLoading ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent"></div>
          <p className="mt-4 text-gray-500">Loading posts...</p>
        </div>
      ) : posts.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-12 text-center">
          <p className="text-gray-500 mb-4">No posts found</p>
          <Button onClick={() => navigate('/posts/new')}>
            Create your first post
          </Button>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Author
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {posts.map((post) => {
                const isMissingInCurrentLocale = !post.has_translation_for_locale
                return (
                  <tr
                    key={post.id}
                    className={
                      isMissingInCurrentLocale ? 'opacity-50 bg-gray-50' : ''
                    }
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="flex items-center gap-2">
                          <div
                            className={`text-sm font-medium ${
                              isMissingInCurrentLocale
                                ? 'text-gray-500'
                                : 'text-gray-900'
                            }`}
                          >
                            {post.translations?.find(
                              (t) => t.locale === currentLanguageCode
                            )?.title || `Post ${post.id}`}
                          </div>
                          {isMissingInCurrentLocale && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700">
                              Missing Translation
                            </span>
                          )}
                        </div>
                        {post.translations?.find(
                          (t) => t.locale === currentLanguageCode
                        )?.slug && (
                          <div
                            className={`text-sm ${
                              isMissingInCurrentLocale
                                ? 'text-gray-400'
                                : 'text-gray-500'
                            }`}
                          >
                            {
                              post.translations.find(
                                (t) => t.locale === currentLanguageCode
                              ).slug
                            }
                          </div>
                        )}
                        {post.available_locales &&
                          post.available_locales.length > 0 && (
                            <div className="flex gap-1 mt-1">
                              {post.available_locales.map((locale) => (
                                <span
                                  key={locale}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700"
                                >
                                  {locale.toUpperCase()}
                                </span>
                              ))}
                            </div>
                          )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          post.is_published
                            ? 'bg-green-100 text-green-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {post.is_published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {post.author?.name || 'Unknown'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => navigate(`/posts/${post.id}`)}
                          className="text-gray-400 hover:text-gray-600"
                          title="Edit"
                        >
                          <Edit className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(post)}
                          className="text-red-400 hover:text-red-600"
                          title="Delete"
                          disabled={deleteMutation.isLoading}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

export default Posts
